import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const pageContent: Record<string, { title: string; description: string; sections: string[] }> = {
  review: {
    title: 'CSSBuy Review 2026',
    description: 'An in-depth review of CSSBuy covering features, pros, cons, safety, and user experience.',
    sections: [
      'CSSBuy Overview',
      'Key Features',
      'Pros and Cons',
      'Safety Analysis',
      'User Experience',
      'Comparison with Alternatives',
    ],
  },
  shipping: {
    title: 'CSSBuy Shipping Cost Guide',
    description: 'Everything you need to know about CSSBuy shipping methods, costs, weight calculations, and tips.',
    sections: [
      'Shipping Methods Overview',
      'Weight vs Volume',
      'Cost by Destination',
      'Shipping Method Comparison',
      'Packaging Tips',
      'Shipping FAQ',
    ],
  },
  qc: {
    title: 'CSSBuy QC Guide',
    description: 'Learn how to read QC photos, what to check, and how to handle quality issues.',
    sections: [
      'What is QC?',
      'How to Read QC Photos',
      'Common Defects to Look For',
      'Sizing Verification',
      'QC Checklist',
      'Handling QC Issues',
    ],
  },
  warehouse: {
    title: 'CSSBuy Warehouse Guide',
    description: 'Understanding CSSBuy warehouse storage, processing times, and workflow.',
    sections: [
      'Warehouse Overview',
      'Storage Duration',
      'Processing Workflow',
      'Consolidation Services',
      'Storage Fees',
      'Warehouse FAQ',
    ],
  },
  safety: {
    title: 'CSSBuy Safety Center',
    description: 'Safety guidelines for using CSSBuy including avoiding scams, fake sites, and payment security.',
    sections: [
      'Avoiding Fake Websites',
      'Suspicious Links',
      'Fake Coupons',
      'Seller Verification',
      'Account Security',
      'Payment Safety',
      'Customs & Restrictions',
    ],
  },
  about: {
    title: 'About CSSBuy Spreadsheet',
    description: 'Learn about our independent resource hub and mission to help shoppers make informed decisions.',
    sections: [
      'Our Mission',
      'What We Offer',
      'Editorial Policy',
      'How We Research',
      'Contact Information',
    ],
  },
  legit: {
    title: 'Is CSSBuy Legit?',
    description: 'A comprehensive analysis of CSSBuy legitimacy, safety, and trustworthiness.',
    sections: [
      'Legitimacy Overview',
      'Company Background',
      'User Reviews',
      'Safety Analysis',
      'Red Flags to Avoid',
      'Final Verdict',
    ],
  },
  sellers: {
    title: 'CSSBuy Sellers Guide',
    description: 'How to find, evaluate, and choose trusted sellers when using CSSBuy.',
    sections: [
      'Finding Sellers',
      'Evaluating Reputation',
      'Seller Comparison',
      'Red Flags',
      'Trusted Seller List',
      'Seller FAQ',
    ],
  },
  coupon: {
    title: 'CSSBuy Coupon Guide',
    description: 'Everything about CSSBuy coupons, discount codes, and how to save money.',
    sections: [
      'How Coupons Work',
      'Finding Valid Codes',
      'Redemption Process',
      'Common Restrictions',
      'Coupon FAQ',
    ],
  },
  discount: {
    title: 'CSSBuy Discount Code Guide',
    description: 'Guide to finding and using CSSBuy discount codes and promotional offers.',
    sections: [
      'Discount Types',
      'Finding Active Codes',
      'How to Apply',
      'Terms & Conditions',
      'Discount FAQ',
    ],
  },
  comparisons: {
    title: 'CSSBuy Comparisons',
    description: 'Compare CSSBuy with alternatives like Superbuy and other agents.',
    sections: [
      'CSSBuy vs Superbuy',
      'Feature Comparison',
      'Shipping Comparison',
      'QC Comparison',
      'Price Comparison',
    ],
  },
  'editorial-policy': {
    title: 'Editorial Policy',
    description: 'How we research, write, and maintain the CSSBuy Spreadsheet guides and reviews.',
    sections: [
      'Our Commitment',
      'Research Process',
      'Content Accuracy',
      'Independence',
    ],
  },
  disclaimer: {
    title: 'Disclaimer',
    description: 'Important information about the nature of this independent resource hub.',
    sections: [
      'Independent Resource',
      'No Affiliation',
      'Use at Your Own Risk',
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'How we handle visitor information and privacy on this website.',
    sections: [
      'Information We Collect',
      'How We Use Data',
      'Third-Party Links',
    ],
  },
  terms: {
    title: 'Terms of Service',
    description: 'The terms and conditions for using this website and its resources.',
    sections: [
      'Acceptance of Terms',
      'Use of Content',
      'Limitation of Liability',
    ],
  },
};

export default function GenericPage() {
  const { page } = useParams<{ page: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const lang = i18n.language || 'en';

  const content = page ? pageContent[page] : null;

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Page Not Found</h1>
          <button onClick={() => navigate(`/${lang}`)} className="btn-primary">
            {t('buttons.backToHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20carefully%20arranged%20flat%20lay%20of%20premium%20fashion%20accessories%20including%20a%20leather%20wallet%2C%20sunglasses%2C%20a%20watch%20and%20a%20folded%20scarf%20on%20a%20soft%20warm%20beige%20linen%20background%2C%20gentle%20natural%20side%20lighting%2C%20refined%20editorial%20e-commerce%20photography%20with%20a%20clean%20minimal%20neutral%20aesthetic&width=1600&height=500&seq=generic-hero&orientation=landscape&nocache=true"
            alt={content.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-foreground-950/70" />
        </div>
        <div className="relative z-10 px-4 md:px-8 lg:px-16 xl:px-24 py-20 md:py-28">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              {content.title}
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto">
              {content.description}
            </p>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 lg:px-16 xl:px-24 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl border border-background-200 p-6 md:p-10">
            <div className="mb-8 p-4 bg-accent-50 rounded-xl border border-accent-100">
              <p className="text-sm text-accent-800 leading-relaxed">
                <i className="ri-information-line mr-1.5 text-accent-600" />
                This page is under development. Our team is working on comprehensive content for this topic. Please check back soon or visit our main resource for more information.
              </p>
            </div>

            <div className="space-y-8">
              {content.sections.map((section, idx) => (
                <div key={idx}>
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                    {section}
                  </h2>
                  <p className="text-foreground-600 leading-relaxed text-sm">
                    Detailed content for {section} is being prepared. This section will cover everything you need to know about {section.toLowerCase()} when using CSSBuy. Check back soon for the full guide.
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-12 p-6 bg-primary-50 rounded-2xl border border-primary-100">
              <h3 className="font-heading text-lg font-bold text-primary-800 mb-3">
                Explore More Resources
              </h3>
              <p className="text-primary-700 text-sm leading-relaxed mb-4">
                Visit our main article for the complete CSSBuy Spreadsheet guide, or browse our homepage for more resources.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate(`/${lang}/cssbuy-spreadsheet`)}
                  className="btn-primary text-sm"
                >
                  <i className="ri-file-list-3-line mr-1.5" />
                  CSSBuy Spreadsheet Guide
                </button>
                <button
                  onClick={() => navigate(`/${lang}`)}
                  className="btn-outline text-sm"
                >
                  <i className="ri-home-4-line mr-1.5" />
                  {t('buttons.backToHome')}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="btn-accent"
            >
              <i className="ri-store-2-line mr-1.5" />
              {t('buttons.buyNow')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}